import { useEffect, useState, useCallback } from 'react';
import { api } from '../lib/api';
import type { ComposerPreviewResponse, StrategyForPreview } from '../types/api';
import { RefreshCw, Calendar, ArrowUp, ArrowDown, ChevronDown } from 'lucide-react';
import { formatDateTimeEastern } from '../lib/dateUtils';

const COMBINED_ID = -1;

export function Preview() {
  const [preview, setPreview] = useState<ComposerPreviewResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState<string>('');
  const [isHistoricalView, setIsHistoricalView] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [strategies, setStrategies] = useState<StrategyForPreview[]>([]);
  const [selectedStrategyId, setSelectedStrategyId] = useState<number>(COMBINED_ID);
  const [strategiesLoading, setStrategiesLoading] = useState(true);

  const deploymentId = selectedStrategyId === COMBINED_ID ? undefined : selectedStrategyId;
  const selectedStrategy = strategies.find((s) => s.id === selectedStrategyId) ?? null;

  const loadStrategies = useCallback(async () => {
    try {
      const list = await api.getStrategiesForPreview();
      setStrategies(list);
    } catch {
      setStrategies([]);
    } finally {
      setStrategiesLoading(false);
    }
  }, []);

  const loadPreview = useCallback(
    async (forceRefresh = false) => {
      setLoading(true);
      setError(null);

      try {
        let data: ComposerPreviewResponse;

        if (forceRefresh && selectedDate && isHistoricalView) {
          data = await api.getHistoricalComposerPreview(selectedDate, false, deploymentId);
        } else if (forceRefresh) {
          if (deploymentId !== undefined) {
            // Recalculate: a deployment's preview is stored once evaluated, like the combined one.
            data = await api.getLatestComposerPreview(deploymentId, true);
          } else {
            data = await api.getComposerPreview();
          }
          setSelectedDate('');
          setIsHistoricalView(false);
        } else {
          data = await api.getLatestComposerPreview(deploymentId);
          setSelectedDate('');
          setIsHistoricalView(false);
        }

        setPreview(data);
        setError(null);
      } catch (err) {
        console.error('Error loading preview:', err);
        const msg = err instanceof Error ? err.message : 'Failed to load preview';
        setError(msg);
        setPreview(null);
      } finally {
        setLoading(false);
      }
    },
    [selectedDate, isHistoricalView, deploymentId]
  );

  const loadHistoricalPreview = useCallback(
    async (date: string, forceCalculate = false) => {
      if (!date) return;
      setSelectedDate(date);
      setIsHistoricalView(true);
      setLoading(true);
      setError(null);
      setPreview(null);

      try {
        const data = await api.getHistoricalComposerPreview(date, !forceCalculate, deploymentId);
        setPreview(data);
        setError(null);
      } catch (err) {
        console.error('Error loading historical preview:', err);
        const msg = err instanceof Error ? err.message : `No preview found for ${date}`;
        setError(msg);
        setPreview(null);
      } finally {
        setLoading(false);
      }
    },
    [deploymentId]
  );

  const handleCalculateNow = () => {
    if (isHistoricalView && selectedDate) {
      loadHistoricalPreview(selectedDate, true);
    } else {
      loadPreview(true);
    }
  };

  const handleClearDate = () => {
    setSelectedDate('');
    setIsHistoricalView(false);
    loadPreview();
  };

  const handleDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const d = e.target.value;
    if (d) loadHistoricalPreview(d);
  };

  const handleStrategyChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const val = Number(e.target.value);
    setSelectedStrategyId(val);
  };

  useEffect(() => {
    loadStrategies();
  }, [loadStrategies]);

  useEffect(() => {
    if (strategiesLoading) return;
    if (isHistoricalView && selectedDate) {
      loadHistoricalPreview(selectedDate);
    } else {
      loadPreview();
    }
  }, [selectedStrategyId, strategiesLoading]);

  const formatPercent = (value: number | null | undefined) => {
    if (value == null) return 'N/A';
    return `${(value * 100).toFixed(2)}%`;
  };
  const formatCurrency = (value: number | null | undefined) => {
    if (value == null) return 'N/A';
    return `$${value.toFixed(2)}`;
  };

  const strategyLabel =
    selectedStrategyId === COMBINED_ID
      ? 'Combined Allocations'
      : selectedStrategy?.name ?? 'Selected Strategy';

  if (loading && strategiesLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="flex flex-col items-center gap-3">
          <RefreshCw className="w-8 h-8 animate-spin text-emerald-600" />
          <span className="text-sm text-gray-600 font-medium">Loading preview...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 tracking-tight">Target Weights Preview</h1>
          <p className="text-gray-600 mt-1 text-base">
            {isHistoricalView
              ? `Historical target weights for ${selectedDate}`
              : `Portfolio target weights — ${strategyLabel}`}
          </p>
        </div>
        <div className="flex flex-wrap gap-3">
          <button
            onClick={() => loadPreview(true)}
            className="inline-flex items-center justify-center gap-2 px-5 py-3 text-base font-semibold rounded-lg bg-emerald-600 text-white shadow-md hover:bg-emerald-700 transition-all"
            disabled={loading}
          >
            <RefreshCw className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>
      </div>

      <div className="bg-white rounded-lg p-6 shadow-md border border-gray-200 space-y-4">
        {/* Strategy selector */}
        <div className="flex flex-col sm:flex-row sm:items-center gap-3">
          <label className="text-base font-semibold text-gray-700 whitespace-nowrap">
            View Strategy:
          </label>
          <div className="relative">
            <select
              value={selectedStrategyId}
              onChange={handleStrategyChange}
              disabled={strategiesLoading}
              className="w-full sm:w-72 appearance-none pl-4 pr-10 py-2 border border-gray-300 rounded-lg bg-white focus:ring-2 focus:ring-emerald-500 focus:border-transparent text-gray-800 font-medium disabled:opacity-60"
            >
              <option value={COMBINED_ID}>Combined Allocations</option>
              {strategies.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name}
                  {s.source_version != null ? ` v${s.source_version}` : ''}
                  {!s.is_enabled
                    ? ' (disabled)'
                    : s.allocation_percent > 0
                    ? ` (${s.allocation_percent.toFixed(0)}%)`
                    : ' (0%)'}
                </option>
              ))}
            </select>
            <ChevronDown className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
          </div>
          {selectedStrategyId !== COMBINED_ID && selectedStrategy && (
            <div className="flex items-center gap-2">
              {selectedStrategy.is_enabled && selectedStrategy.allocation_percent > 0 ? (
                <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-bold bg-emerald-600 text-white">
                  {selectedStrategy.allocation_percent.toFixed(0)}% Deployed
                </span>
              ) : (
                <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-bold bg-gray-400 text-white">
                  {selectedStrategy.is_enabled ? 'Not allocated' : 'Disabled'}
                </span>
              )}
              {selectedStrategy.trading_mode && (
                <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-bold border ${
                  selectedStrategy.trading_mode === 'LIVE'
                    ? 'bg-red-100 text-red-800 border-red-300'
                    : 'bg-blue-100 text-blue-800 border-blue-300'
                }`}>
                  {selectedStrategy.trading_mode}
                </span>
              )}
            </div>
          )}
          {strategies.length === 0 && !strategiesLoading && (
            <p className="text-sm text-gray-500 italic">No deployments yet — combined view only</p>
          )}
        </div>

        {/* Date picker */}
        <div className="flex flex-col sm:flex-row sm:items-center gap-3">
          <Calendar className="w-5 h-5 text-gray-600 shrink-0" />
          <label htmlFor="preview-date" className="text-base font-semibold text-gray-700 whitespace-nowrap">
            View Historical Date:
          </label>
          <input
            id="preview-date"
            type="date"
            value={selectedDate}
            onChange={handleDateChange}
            max={new Date().toISOString().split('T')[0]}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
          />
          {selectedDate && (
            <button
              onClick={handleClearDate}
              className="text-sm text-gray-600 hover:text-gray-900 underline"
            >
              Clear
            </button>
          )}
        </div>
      </div>

      {loading && (
        <div className="flex items-center justify-center h-32">
          <div className="flex flex-col items-center gap-3">
            <RefreshCw className="w-7 h-7 animate-spin text-emerald-600" />
            <span className="text-sm text-gray-600 font-medium">Loading weights...</span>
          </div>
        </div>
      )}

      {!loading && error && !preview && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
          <div className="text-center space-y-4">
            <div className="text-blue-800">
              <p className="text-lg font-semibold">No Preview Available</p>
              <p className="text-base mt-2">
                {isHistoricalView
                  ? `No stored preview found for ${selectedDate}${selectedStrategyId !== COMBINED_ID ? ` (${strategyLabel})` : ''}.`
                  : `No preview has been calculated yet for today${selectedStrategyId !== COMBINED_ID ? ` (${strategyLabel})` : ''}.`}
              </p>
              {/* The server's reason, verbatim. A calculation that FAILED — Schwab not connected,
                  a deployment refused for its hash — otherwise read as one never attempted. */}
              <p className="text-sm text-blue-900 mt-2 break-words">{error}</p>
              <p className="text-sm text-blue-600 mt-1">
                {selectedStrategyId !== COMBINED_ID
                  ? "This deployment's pinned version is evaluated on demand, the same way a rebalance evaluates it."
                  : 'Target weights are automatically calculated by scheduled bot runs during market hours.'}
              </p>
            </div>
            <button
              onClick={handleCalculateNow}
              disabled={loading}
              className="inline-flex items-center justify-center gap-2 px-6 py-3 text-base font-semibold rounded-lg bg-blue-600 text-white shadow-md hover:bg-blue-700 transition-all disabled:opacity-50"
            >
              <RefreshCw className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
              Calculate Now
            </button>
          </div>
        </div>
      )}

      {!loading && error && preview && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 text-center">
          <p className="text-gray-700 text-base">{error}</p>
        </div>
      )}

      {!loading && preview && (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
            <div className="bg-white rounded-lg p-6 shadow-md border border-gray-200 transition-all duration-200 hover:shadow-lg hover-lift">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-base font-semibold text-gray-600">Strategy</p>
                  <p className="text-xl font-bold text-gray-900 mt-2 break-all">
                    {selectedStrategyId === COMBINED_ID
                      ? 'Combined Allocations'
                      : selectedStrategy?.name ?? preview.symphony_id}
                  </p>
                  {selectedStrategyId === COMBINED_ID && (
                    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-bold bg-purple-600 text-white mt-2">
                      Multi-Strategy
                    </span>
                  )}
                  {selectedStrategyId !== COMBINED_ID && selectedStrategy && selectedStrategy.is_enabled && selectedStrategy.allocation_percent > 0 && (
                    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-bold bg-emerald-600 text-white mt-2">
                      {selectedStrategy.allocation_percent.toFixed(0)}% Capital Deployed
                    </span>
                  )}
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg p-6 shadow-md border border-gray-200 transition-all duration-200 hover:shadow-lg hover-lift">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-base font-semibold text-gray-600">Fetched At</p>
                  <p className="text-lg font-bold text-gray-900 mt-2">
                    {formatDateTimeEastern(preview.fetched_at)}
                  </p>
                  {isHistoricalView && (
                    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-semibold bg-amber-100 text-amber-800 mt-1">
                      Historical: {selectedDate}
                    </span>
                  )}
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg p-6 shadow-md border border-gray-200 transition-all duration-200 hover:shadow-lg hover-lift">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-base font-semibold text-gray-600">Data Type</p>
                  <p className="text-lg font-bold mt-2">
                    <span className={preview.is_demo ? 'text-yellow-600' : 'text-emerald-700'}>
                      {preview.is_demo ? 'Demo Data' : 'Live Data'}
                    </span>
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-md border border-gray-200 overflow-hidden">
            <div className="p-6 border-b border-gray-200 bg-gradient-to-r from-emerald-50 to-emerald-100">
              <h2 className="text-2xl font-bold text-gray-900">Target Portfolio Weights</h2>
              <div className="flex flex-wrap items-center gap-2 mt-1">
                <span className="text-gray-700">{preview.target_weights.length} positions</span>
                <span className="text-gray-400">•</span>
                <span className="text-gray-700">
                  Portfolio Value: {formatCurrency(preview.notional_portfolio_size || 20000)}
                </span>
                {preview.notional_portfolio_size && preview.notional_portfolio_size !== 20000 ? (
                  <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-bold bg-emerald-600 text-white">
                    Live Balance
                  </span>
                ) : (
                  <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-bold bg-gray-500 text-white">
                    Notional $20K
                  </span>
                )}
              </div>
              {preview.unallocated_funds !== undefined && (
                <p className="text-gray-700 mt-1 font-semibold">
                  Unallocated Funds: {formatCurrency(preview.unallocated_funds)}
                </p>
              )}
            </div>

            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="text-left p-4 text-sm font-semibold text-gray-700 uppercase tracking-wider">
                      Symbol
                    </th>
                    <th className="text-right p-4 text-sm font-semibold text-gray-700 uppercase tracking-wider">
                      Current Price
                    </th>
                    <th className="text-right p-4 text-sm font-semibold text-gray-700 uppercase tracking-wider">
                      Previous Weight
                    </th>
                    <th className="text-right p-4 text-sm font-semibold text-gray-700 uppercase tracking-wider">
                      Target Weight
                    </th>
                    <th className="text-right p-4 text-sm font-semibold text-gray-700 uppercase tracking-wider">
                      Target Quantity
                    </th>
                    <th className="text-right p-4 text-sm font-semibold text-gray-700 uppercase tracking-wider">
                      Target Allocation
                    </th>
                    <th className="text-right p-4 text-sm font-semibold text-gray-700 uppercase tracking-wider">
                      Change
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {preview.target_weights
                    .sort((a, b) => b.target_weight - a.target_weight)
                    .map((weight) => {
                      const change = weight.target_weight - weight.prev_weight;
                      const isIncrease = change > 0;
                      const isDecrease = change < 0;

                      return (
                        <tr key={weight.symbol} className="hover:bg-gray-50 transition-colors">
                          <td className="p-4">
                            <span className="font-bold text-lg text-gray-900">{weight.symbol}</span>
                          </td>
                          <td className="text-right p-4 text-base text-gray-700 font-medium">
                            {formatCurrency(weight.current_price)}
                          </td>
                          <td className="text-right p-4 text-base text-gray-700 font-medium">
                            {formatPercent(weight.prev_weight)}
                          </td>
                          <td className="text-right p-4 text-base font-bold text-emerald-700">
                            {formatPercent(weight.target_weight)}
                          </td>
                          <td className="text-right p-4 text-base text-gray-700 font-medium">
                            {weight.target_quantity !== undefined ? weight.target_quantity : 'N/A'}
                          </td>
                          <td className="text-right p-4 text-base text-gray-700 font-medium">
                            {formatCurrency(weight.target_allocation)}
                          </td>
                          <td className="text-right p-4">
                            <div className="flex items-center justify-end gap-1">
                              {isIncrease && <ArrowUp className="w-4 h-4 text-emerald-600" />}
                              {isDecrease && <ArrowDown className="w-4 h-4 text-red-600" />}
                              <span
                                className={`text-base font-semibold ${
                                  isIncrease
                                    ? 'text-emerald-600'
                                    : isDecrease
                                    ? 'text-red-600'
                                    : 'text-gray-600'
                                }`}
                              >
                                {change >= 0 ? '+' : ''}
                                {formatPercent(change)}
                              </span>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}

      {!loading && !preview && !error && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6 text-center">
          <p className="text-gray-700 text-lg">
            No preview data available. Click "Refresh" to get the latest target weights.
          </p>
        </div>
      )}
    </div>
  );
}
